import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, Activity, Heart, Moon, Sparkles } from 'lucide-react';
import { usePeriodStore } from '../../store/periodStore';

export function CycleInsights() {
  const { cycles, symptoms, moods } = usePeriodStore();

  const insights = [
    {
      title: 'Cycle Pattern',
      description: 'Your cycle has been consistent at 28 days. This regularity indicates balanced hormonal health.',
      icon: <Activity className="w-5 h-5 text-primary" />,
      trend: '+2 days more regular',
      color: 'from-primary/20 to-purple-500/20'
    },
    {
      title: 'Mood Patterns',
      description: 'During ovulation, serotonin levels naturally increase, explaining your elevated mood and energy.',
      icon: <Brain className="w-5 h-5 text-purple-500" />,
      trend: 'Consistent pattern',
      color: 'from-purple-500/20 to-pink-500/20'
    },
    {
      title: 'Hormonal Rhythm',
      description: 'Your estrogen peaks align perfectly with your most productive days. Consider scheduling important tasks during this time.',
      icon: <Moon className="w-5 h-5 text-pink-500" />,
      trend: 'Optimal balance',
      color: 'from-pink-500/20 to-rose-500/20'
    },
    {
      title: 'Energy Cycles',
      description: 'Your energy levels follow a natural circadian rhythm, peaking during the follicular phase.',
      icon: <Sparkles className="w-5 h-5 text-amber-500" />,
      trend: 'Improving trend',
      color: 'from-amber-500/20 to-yellow-500/20'
    }
  ];

  return (
    <div className="space-y-4">
      {insights.map((insight, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="theme-card p-6 relative overflow-hidden group"
        >
          {/* Animated Background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${insight.color} opacity-50 group-hover:opacity-75 transition-opacity`} />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="relative">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  {insight.icon}
                  <div className="absolute inset-0 bg-current opacity-20 blur-xl rounded-full" />
                </div>
                <h3 className="font-medium">{insight.title}</h3>
              </div>
              <div className="flex items-center gap-1 text-sm text-success">
                <TrendingUp className="w-4 h-4" />
                <span>{insight.trend}</span>
              </div>
            </div>

            {/* Content */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {insight.description}
            </p>

            {/* Interactive Elements */}
            <div className="mt-4 flex items-center gap-4">
              <button className="text-xs text-primary hover:text-primary/80 transition-colors">
                Learn More
              </button>
              <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                View History
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}